import { useEffect, useState } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IPrincipal, ITableColumn } from "@interfaces/common.interfaces";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import ReportSection from "components/reports-common/report-section/report-section";
import {
    IWorkspaceSummaryByOwnerReportDetails,
    IWorkspaceSummaryByOwnerReportItem,
    IWorkspaceSummaryByOwnerReportRequestModel
} from "@interfaces/reports/summary-reports.interfaces";
import WorkspaceSummaryByOwnerReportSummary from "components/reports/summary-reports/workspace-summary-by-owner-report/workspace-summary-by-owner-report-summary";
import PrincipalPicker from "components/controls/principal-picker/principal-picker";
import WorkspaceLinkCell from "components/reports-common/report-results-grid-cells/workspace-link-cell/workspace-link-cell";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import SizeCell from "components/reports-common/report-results-grid-cells/size-cell/size-cell";
import StoragePercentageCell from "components/reports-common/report-results-grid-cells/storage-percentage-cell/storage-percentage-cell";
import CheckedCell from "components/reports-common/report-results-grid-cells/checked-cell/checked-cell";
import { getWorkspaceTypeKey } from "lib/workspaces";
import { usePPA } from "context/ppa-context";
import { PPAPermissions } from "lib/permissions";

const reportUrl: string = "/api/reports/workspace-summary-by-owner";

export default function WorkspaceSummaryByOwnerReport() {
    const { t } = useL10n<ILocaleStrings>();
    const { currentUser, hasPermission } = usePPA();

    const [model, setModel] = useState<IWorkspaceSummaryByOwnerReportRequestModel>({});
    const [selectedUser, setSelectedUser] = useState<IPrincipal>();
    const [mineOnly, setMineOnly] = useState<boolean>(false);

    useEffect(() => {
        setSelectedUser(currentUser);
        if (
            hasPermission(PPAPermissions.RequestWorkspaceSummaryByOwnerReportMineOnly) &&
            !hasPermission(PPAPermissions.RequestWorkspaceSummaryByOwnerReportAny)
        ) {
            setMineOnly(true);
        }
    }, []);

    const onUserChange = (value?: IPrincipal) => {
        setModel({ ...model, owner: { upn: value?.upn } });
        setSelectedUser(value);
    };

    // Define table columns
    const cols: Array<ITableColumn<IWorkspaceSummaryByOwnerReportItem>> = [
        // Workspace (link column)
        {
            key: "workspaceName",
            label: t("ppaWorkspace"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                <WorkspaceLinkCell
                    name={item.workspaceName}
                    url={item.workspaceUrl}
                    template={item.workspaceTemplate}
                    type={item.workspaceType}
                />
            )
        },
        // Workspace URL (export only)
        {
            key: "workspaceUrl",
            label: t("ppaWorkspaceUrl"),
            showInGrid: false,
            showInExport: true
        },
        // Workspace Type
        {
            key: "workspaceType",
            label: t("ppaWorkspaceType"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 130,
            renderCell: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            ),
            getExportValue: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                t(getWorkspaceTypeKey(item.workspaceType))
            )
        },
        // Workspace Template (export only)
        {
            key: "workspaceTemplate",
            label: t("ppaWorkspaceTemplate"),
            showInGrid: false,
            showInExport: true
        },
        // Inactive (checkbox)
        {
            key: "isInactive",
            label: t("ppaInactive"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 90,
            renderCell: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                <CheckedCell columnId={key} item={item} checkedValue={true} />
            ),
            getExportValue: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                item.isInactive ? t("ppaYes") : t("ppaNo")
            )
        },
        // Orphaned (checkbox)
        {
            key: "isOrphaned",
            label: t("ppaOrphaned"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 100,
            renderCell: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                <CheckedCell columnId={key} item={item} checkedValue={true} />
            ),
            getExportValue: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                item.isOrphaned ? t("ppaYes") : t("ppaNo")
            )
        },
        // Last Modified
        {
            key: "lastModified",
            label: t("lastModified"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                <DateTimeCell columnId={key} item={item} pattern="PPp" />
            )
        },
        // Total Size
        {
            key: "totalSize",
            label: t("ppaTotalSize"),
            showInGrid: true,
            showInExport: false,
            defaultWidth: 120,
            renderCell: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => (
                <SizeCell value={item.totalSize} valueUnits="B" />
            )
        },
        // Total Size Bytes (export only)
        {
            key: "totalSizeBytes",
            label: t("ppaTotalSizeBytes"),
            showInGrid: false,
            showInExport: true,
            getExportValue: (key: string, item: IWorkspaceSummaryByOwnerReportItem) => item.totalSize.toString()
        },
        // % of Quota
        {
            key: "percentageOfQuota",
            label: t("ppaPercentageOfQuota"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 180,
            renderCell: (key: string, item: IWorkspaceSummaryByOwnerReportItem, details?: IWorkspaceSummaryByOwnerReportDetails) => (
                <StoragePercentageCell
                    percentageUsed={item.percentageOfQuota}
                    usedValue={item.totalSize}
                    usedValueUnits="B"
                    maxValue={details?.storageQuotaMB || 0}
                    maxValueUnits="MB"
                />
            )
        },
        // User Visits (export only)
        {
            key: "userVisits",
            label: t("ppaUserVisits"),
            showInGrid: false,
            showInExport: true
        },
        // Active Users (export only)
        {
            key: "activeUsers",
            label: t("ppaActiveUsers"),
            showInGrid: false,
            showInExport: true
        }
    ];

    const isModelValid = (): boolean => {
        return model.owner?.upn ? true : false;
    };

    const reportSectionProps: IReportSectionProps<IWorkspaceSummaryByOwnerReportItem, IWorkspaceSummaryByOwnerReportDetails> = {
        columns: cols,
        summaryComponent: <WorkspaceSummaryByOwnerReportSummary />,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaWorkspaceSummaryByOwnerReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaWorkspaceSummaryByOwnerReportDefaultExportFilenamePrefix"
    };

    return (
        <ReportSection {...reportSectionProps}>
            <PrincipalPicker
                onChange={onUserChange}
                model={selectedUser}
                label="ppaWorkspaceRoleTypeOwner"
                readonly={mineOnly}
                principalTypes={["User"]}
            />
        </ReportSection>
    );
}
